/**
 * AutoPostingBridge — Phase 2.
 *
 * Mounts once inside <GeneralLedgerProvider>. Observes AppData
 * (jobs.financials.invoices, jobs.financials.payments, payRunRecords,
 *  jobs with installStatus === "Install Complete") and posts a balanced
 * journal entry for any event that hasn't been posted yet.
 *
 * Idempotency: tracks already-posted (kind,id) keys in a ref, seeded
 * from existing journalEntries.sourceRef on mount.
 *
 * This component renders nothing. Zero changes to existing cockpits.
 */
import { useEffect, useRef } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useGeneralLedger } from "./GeneralLedgerContext";
import {
  ruleInvoiceIssued,
  rulePaymentReceived,
  rulePayRunReleased,
  ruleJobCompleted,
  normalizeTrade,
  resolveTradeAccount,
} from "./postingRules";

const keyFor = (kind: string, id: string) => `${kind}::${id}`;

export function AutoPostingBridge() {
  const appData = useAppData();
  const { jobs } = appData;
  const payRunRecords: any[] = (appData as any).payRunRecords ?? [];
  const { journalEntries, postJournal } = useGeneralLedger();
  const postedRef = useRef<Set<string>>(new Set());
  // Snapshot of journalEntries used by the sweep — refreshed only when the
  // sweep needs it, not on every JE mutation. Prevents the bridge from
  // re-iterating jobs × invoices on every post it itself triggers.
  const jeSnapshotRef = useRef(journalEntries);

  // EFFECT 1 — keep the dedupe set in sync with the GL.
  // Pulled out of the sweep so a JE added after mount (seed-GL hook race,
  // manual JE keyed to a doc id) is recognized without re-running posting.
  useEffect(() => {
    for (const je of journalEntries) {
      if (je.sourceRef) postedRef.current.add(keyFor(je.sourceRef.kind, je.sourceRef.id));
    }
    jeSnapshotRef.current = journalEntries;
  }, [journalEntries]);

  // EFFECT 2 — sweep ONLY when operational data changes. journalEntries is
  // intentionally NOT in the deps; postJournal updates it, but we already
  // record success in postedRef so re-sweep would be wasted work.
  useEffect(() => {
    const entries = jeSnapshotRef.current;
    const trySafe = (fn: () => void, label: string) => {
      try { fn(); } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[AutoPostingBridge] ${label}:`, e instanceof Error ? e.message : e);
      }
    };

    for (const job of jobs) {
      const invoices = job.financials?.invoices ?? [];
      const payments = job.financials?.payments ?? [];

      for (const inv of invoices) {
        const k = keyFor("invoice", inv.id);
        if (postedRef.current.has(k)) continue;
        const rule = ruleInvoiceIssued(job, inv);
        if (!rule) continue;
        trySafe(() => { postJournal(rule); postedRef.current.add(k); }, `invoice ${inv.id}`);
      }

      for (const pay of payments) {
        const k = keyFor("payment", pay.id);
        if (postedRef.current.has(k)) continue;
        const rule = rulePaymentReceived(job, pay);
        if (!rule) continue;
        trySafe(() => { postJournal(rule); postedRef.current.add(k); }, `payment ${pay.id}`);
      }

      const jc = keyFor("job_complete", job.id);
      const isComplete = job.installStatus === "Install Complete" || job.currentStage === "Completed";
      if (isComplete && !postedRef.current.has(jc)) {
        const wipMat = computeJobWip(job.id, "material", entries);
        const wipLab = computeJobWip(job.id, "labor", entries);
        const deferredRevenueBalance = computeDeferredRevenue(job.id, entries);
        const rule = ruleJobCompleted(job, {
          wipMaterial: wipMat,
          wipLabor: wipLab,
          deferredRevenueBalance,
          contractValue: job.financials?.contractValue ?? 0,
        });
        if (rule) {
          trySafe(() => { postJournal(rule); postedRef.current.add(jc); }, `job_complete ${job.id}`);
        }
      }
    }

    for (const rec of payRunRecords) {
      const k = keyFor("pay_run", rec.id);
      if (postedRef.current.has(k)) continue;
      const job = jobs.find(j => j.id === rec.jobId);
      const rule = rulePayRunReleased(job, rec);
      if (!rule) continue;
      trySafe(() => { postJournal(rule); postedRef.current.add(k); }, `pay_run ${rec.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, payRunRecords, postJournal]);

  return null;
}

/**
 * Compute current WIP balance for a job, split by material vs labor.
 *
 * Bucketing precedence (most specific first):
 *   1. Line carries an explicit `wipBucket` field (preferred — set this on new postings).
 *   2. Memo carries the canonical "[material]" / "[labor]" tag (legacy).
 *   3. Source = "payroll" → labor.
 *   4. Memo substring contains "labor" → labor; else material. (Avoid — fragile.)
 */
function computeJobWip(
  jobId: string,
  bucket: "material" | "labor",
  entries: ReturnType<typeof useGeneralLedger>["journalEntries"],
): number {
  let total = 0;
  for (const je of entries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (l.accountId !== "1200" || l.jobId !== jobId) continue;
      let isLabor: boolean;
      if (l.wipBucket) {
        isLabor = l.wipBucket === "labor";
      } else {
        const memo = (l.memo ?? "").toLowerCase();
        if (memo.includes("[labor]")) isLabor = true;
        else if (memo.includes("[material]")) isLabor = false;
        else if (je.source === "payroll") isLabor = true;
        else isLabor = memo.includes("labor");
      }
      const wantThis = bucket === "labor" ? isLabor : !isLabor;
      if (!wantThis) continue;
      total += l.side === "debit" ? l.amount : -l.amount;
    }
  }
  return Math.max(0, Math.round(total * 100) / 100);
}

/**
 * Compute the current Deferred Revenue (2200) balance for a job:
 *   sum(credits) - sum(debits) where line.jobId === jobId.
 * Used to size the job-completion release so it exactly matches what was
 * actually deferred from prior invoice postings.
 */
function computeDeferredRevenue(
  jobId: string,
  entries: ReturnType<typeof useGeneralLedger>["journalEntries"],
): number {
  let bal = 0;
  for (const je of entries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (l.accountId !== "2200" || l.jobId !== jobId) continue;
      bal += l.side === "credit" ? l.amount : -l.amount;
    }
  }
  return Math.max(0, Math.round(bal * 100) / 100);
}
